import { Button, Card, Col, Row, Space, Statistic, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';

type CrmBoard = {
  summary: {
    enquiries: number;
    applications: number;
    open_pipeline: number;
    converted: number;
    seats_available: number;
  };
  funnel: Array<{ status: string; count: number; list_path: string }>;
  sources: Array<{
    source_channel: string;
    enquiries: number;
    applications: number;
    offered_or_beyond: number;
    converted: number;
    rejected: number;
    enquiry_to_application_pct: number | null;
    application_to_offer_pct: number | null;
    application_to_converted_pct: number | null;
  }>;
  seats: Array<{
    session_id: string;
    grade_level_id: string;
    session_code?: string;
    session_name?: string;
    grade_code?: string;
    grade_name?: string;
    capacity: number;
    enrolled: number;
    reserved: number;
    available: number;
    list_path: string;
  }>;
};

function pct(value: number | null | undefined): string {
  return value == null ? '—' : `${value}%`;
}

export default function AdmissionsCrmPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const session = params.get('session') || undefined;
  const grade = params.get('grade') || undefined;

  const query = useQuery({
    queryKey: ['education-admissions-crm', session, grade],
    queryFn: () =>
      api.get<CrmBoard>('/education/admissions/crm', {
        params: {
          session_id: session,
          grade_level_id: grade,
        },
      }),
  });

  const board = query.data?.data;
  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <EducationPageChrome
      title={t('admissionsCrm')}
      description={t('admissionsCrmIntro')}
      tags={board ? <Tag>{t('openPipelineCount', { count: board.summary.open_pipeline })}</Tag> : undefined}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/admissions')}>{t('admissionsTitle')}</Button>
          <Button type="primary" onClick={() => navigate('/education/admissions/new')}>
            {t('newApplication')}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Space wrap>
            <EducationSessionSelect
              placeholder={t('session')}
              value={session}
              onChange={(value) => setFilter('session', (value as string) || undefined)}
              style={{ width: 220 }}
            />
            <EducationGradeSelect
              placeholder={t('grade')}
              value={grade}
              onChange={(value) => setFilter('grade', (value as string) || undefined)}
              style={{ width: 200 }}
            />
          </Space>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={12} md={4}>
            <Card size="small" loading={query.isLoading}>
              <Statistic title={t('enquiries')} value={board?.summary.enquiries ?? 0} />
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card size="small" loading={query.isLoading}>
              <Statistic title={t('applications')} value={board?.summary.applications ?? 0} />
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card size="small" loading={query.isLoading}>
              <Statistic title={t('openPipeline')} value={board?.summary.open_pipeline ?? 0} />
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card size="small" loading={query.isLoading}>
              <Statistic title={t('converted')} value={board?.summary.converted ?? 0} />
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card size="small" loading={query.isLoading}>
              <Statistic title={t('seatsAvailable')} value={board?.summary.seats_available ?? 0} />
            </Card>
          </Col>
        </Row>

        <Card size="small" title={t('admissionFunnel')}>
          <Table
            rowKey="status"
            size="small"
            loading={query.isLoading}
            pagination={false}
            dataSource={board?.funnel ?? []}
            onRow={(row) => ({
              onClick: () => navigate(row.list_path),
              style: { cursor: row.count > 0 ? 'pointer' : 'default' },
            })}
            columns={[
              {
                title: t('status'),
                dataIndex: 'status',
                render: (value: string) => <Tag>{value}</Tag>,
              },
              { title: t('count'), dataIndex: 'count', width: 100 },
              {
                title: t('actions'),
                width: 140,
                render: (_: unknown, row: CrmBoard['funnel'][number]) =>
                  row.count > 0 ? (
                    <Button size="small" type="link" onClick={() => navigate(row.list_path)}>
                      {t('openList')}
                    </Button>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        </Card>

        <Card size="small" title={t('sourceConversion')}>
          <Table
            rowKey="source_channel"
            size="small"
            loading={query.isLoading}
            pagination={{ pageSize: 10 }}
            dataSource={board?.sources ?? []}
            columns={[
              { title: t('sourceChannel'), dataIndex: 'source_channel' },
              { title: t('enquiries'), dataIndex: 'enquiries', width: 100 },
              { title: t('applications'), dataIndex: 'applications', width: 110 },
              { title: t('offeredOrBeyond'), dataIndex: 'offered_or_beyond', width: 120 },
              { title: t('converted'), dataIndex: 'converted', width: 100 },
              { title: t('rejected'), dataIndex: 'rejected', width: 90 },
              {
                title: t('enquiryToApp'),
                width: 110,
                render: (_: unknown, row: CrmBoard['sources'][number]) => pct(row.enquiry_to_application_pct),
              },
              {
                title: t('appToOffer'),
                width: 110,
                render: (_: unknown, row: CrmBoard['sources'][number]) => pct(row.application_to_offer_pct),
              },
              {
                title: t('appToConverted'),
                width: 120,
                render: (_: unknown, row: CrmBoard['sources'][number]) => pct(row.application_to_converted_pct),
              },
            ]}
          />
        </Card>

        <Card size="small" title={t('seatAvailability')}>
          <Table
            rowKey={(row) => `${row.session_id}-${row.grade_level_id}`}
            size="small"
            loading={query.isLoading}
            pagination={{ pageSize: 15 }}
            dataSource={board?.seats ?? []}
            onRow={(row) => ({
              onClick: () => navigate(row.list_path),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: t('session'),
                render: (_: unknown, row: CrmBoard['seats'][number]) =>
                  row.session_name || row.session_code || '—',
              },
              {
                title: t('grade'),
                render: (_: unknown, row: CrmBoard['seats'][number]) => row.grade_name || row.grade_code || '—',
              },
              { title: t('capacity'), dataIndex: 'capacity', width: 90 },
              { title: t('enrolled'), dataIndex: 'enrolled', width: 90 },
              { title: t('reservedSeats'), dataIndex: 'reserved', width: 100 },
              {
                title: t('available'),
                dataIndex: 'available',
                width: 100,
                render: (value: number) => <Tag color={value > 0 ? 'green' : 'red'}>{value}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
